// biome-ignore-all lint/a11y: TODO - address a11y issues later
import { InfoCircledIcon } from "@radix-ui/react-icons";
import { Loader2, Search, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ActiveFiltersDisplay } from "@/components/active-filters-display";
import { FilterPopover, type FilterValue } from "@/components/filter-popover";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { InfoTooltip } from "@/components/ui/info-tooltip";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SortIcon } from "@/components/ui/sort-icon";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useAccountActivities } from "@/hooks/api/use-account-activities";
import {
  type Opportunity,
  useAccountOpportunities,
} from "@/hooks/api/use-account-opportunities";
import {
  type Person,
  useAccountPersons,
} from "@/hooks/api/use-account-persons";
import { useAccountSelection } from "@/hooks/use-account-selection";
import type { CustomBuyingGroup } from "@/hooks/use-buying-group-selection";
import { useCustomBuyingGroupFilters } from "@/hooks/use-custom-buying-group-filters";
import { useDebounce } from "@/hooks/use-debounce";
import { getStageVariant } from "@/lib/badge-utils";
import { formatDate } from "@/lib/date-formatting";
import {
  extractArrayFromFilter,
  extractNullFilterFromFilter,
  extractTextFromFilter,
} from "@/lib/filter-value-utils";
import { cn } from "@/lib/utils";
import type { SortDirection, UnifiedTableColumn } from "@/types/table-column";
import { getOpportunityColumns } from "./custom-buying-group-opportunity-columns";
import { getPersonColumns } from "./custom-buying-group-person-columns";

// const opportunityKeyExtractor = (opportunity: Opportunity) => opportunity.id;
// const personKeyExtractor = (person: PersonFragment) => person.id;

// FilterValue type is likely imported from @/components/filter-popover

interface SelectableOpportunitiesProps {
  accountId?: string;
  selectedIds: string[];
  onSelectionChange: (ids: string[]) => void;
}

function SelectableOpportunities({
  accountId,
  selectedIds,
  onSelectionChange,
}: SelectableOpportunitiesProps) {
  // Use default mode for these requests - we want all opportunities
  const { data: opportunities, isLoading } = useAccountOpportunities(
    accountId,
    undefined,
    undefined,
    "default", // Always use default mode here
  );
  const [searchInputValue, setSearchInputValue] = useState<string>("");
  const debouncedSearchQuery = useDebounce(searchInputValue, 300);

  // Get column definitions
  const columns = useMemo<UnifiedTableColumn[]>(
    () => getOpportunityColumns() as UnifiedTableColumn[],
    [],
  );

  // Get filter state
  const {
    filters,
    updateFilter,
    clearFilter,
    clearAllFilters,
    hasFilters,
    getDefaultSortSettings,
  } = useCustomBuyingGroupFilters("opportunities");

  // Initialize sort config from filters
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: SortDirection;
  }>({
    key: (filters.sortColumn as string) || getDefaultSortSettings().sortColumn,
    direction:
      (filters.sortDirection as SortDirection) ||
      getDefaultSortSettings().sortDirection,
  });

  // Update filter when debounced search query changes
  useEffect(() => {
    if (debouncedSearchQuery) {
      updateFilter("search", debouncedSearchQuery);
    } else if (filters.search) {
      clearFilter("search");
    }
  }, [debouncedSearchQuery, clearFilter, updateFilter, filters.search]);

  // Filtered data based on current filters
  const filteredOpportunities = useMemo(() => {
    if (!opportunities) return [] as Opportunity[];

    let filteredData: Opportunity[] = [...opportunities];

    // Apply search filter
    if (filters.search && typeof filters.search === "string") {
      const searchLower = (filters.search as string).toLowerCase();
      filteredData = filteredData.filter((opp) =>
        opp.name.toLowerCase().includes(searchLower),
      );
    }

    // Apply column-specific filters
    Object.entries(filters).forEach(([key, value]) => {
      if (key === "sortColumn" || key === "sortDirection" || key === "search") {
        return;
      }

      const column = columns.find((col) => col.key === key);
      if (!column) return;

      switch (column.filterType) {
        case "text": {
          const text = extractTextFromFilter(value as FilterValue);
          const nullFilter = extractNullFilterFromFilter(value as FilterValue);

          if (nullFilter === "is_null") {
            filteredData = filteredData.filter(
              (opp) => opp[key as keyof Opportunity] === null,
            );
            break;
          }
          if (nullFilter === "is_not_null") {
            filteredData = filteredData.filter(
              (opp) => opp[key as keyof Opportunity] !== null,
            );
          }

          if (text) {
            filteredData = filteredData.filter((opp) => {
              const oppValue = opp[key as keyof Opportunity];
              return oppValue
                ?.toString()
                .toLowerCase()
                .includes(text.toLowerCase());
            });
          }
          break;
        }
        case "select": {
          const values = extractArrayFromFilter(value as FilterValue);
          const nullFilter = extractNullFilterFromFilter(value as FilterValue);

          if (nullFilter === "is_null") {
            filteredData = filteredData.filter(
              (opp) => opp[key as keyof Opportunity] === null,
            );
            break;
          }
          if (nullFilter === "is_not_null") {
            filteredData = filteredData.filter(
              (opp) => opp[key as keyof Opportunity] !== null,
            );
          }

          if (values.length > 0) {
            filteredData = filteredData.filter((opp) => {
              const oppValue = opp[key as keyof Opportunity];
              return oppValue && values.includes(oppValue.toString());
            });
          }
          break;
        }
        case "date":
          if (typeof value === "object" && value !== null) {
            const dateFilter = value as { from?: string; to?: string };
            filteredData = filteredData.filter((opp) => {
              const oppDate = opp[key as keyof Opportunity];
              if (typeof oppDate !== "string") return false;

              const date = new Date(oppDate);
              let passes = true;

              if (dateFilter.from) {
                passes = passes && date >= new Date(dateFilter.from);
              }

              if (dateFilter.to) {
                passes = passes && date <= new Date(dateFilter.to);
              }

              return passes;
            });
          }
          break;
      }
    });

    // Apply sorting based on current sort config
    if (sortConfig.key && sortConfig.direction) {
      filteredData.sort((a: Opportunity, b: Opportunity) => {
        const key = sortConfig.key;
        const aValue = a[key as keyof Opportunity];
        const bValue = b[key as keyof Opportunity];

        // Handle specific case for date sorting first
        if (key === "created_date" || key === "close_date") {
          // Ensure value is a valid date string before parsing
          const dateA = typeof aValue === "string" ? new Date(aValue) : null;
          const dateB = typeof bValue === "string" ? new Date(bValue) : null;

          // Handle potential null dates
          if (dateA === null && dateB === null) return 0;
          if (dateA === null) return sortConfig.direction === "asc" ? 1 : -1;
          if (dateB === null) return sortConfig.direction === "asc" ? -1 : 1;

          if (dateA < dateB) {
            return sortConfig.direction === "asc" ? -1 : 1;
          }
          if (dateA > dateB) {
            return sortConfig.direction === "asc" ? 1 : -1;
          }
          return 0;
        }

        // General comparison for other types (string, number, boolean)
        // Handle nulls: sort nulls last
        if (aValue === null && bValue === null) return 0;
        if (aValue === null) return sortConfig.direction === "asc" ? 1 : -1;
        if (bValue === null) return sortConfig.direction === "asc" ? -1 : 1;

        // After null checks, TypeScript still thinks values could be undefined
        // Cast to ensure type safety
        const aVal = aValue as string | number;
        const bVal = bValue as string | number;

        if (aVal < bVal) {
          return sortConfig.direction === "asc" ? -1 : 1;
        }
        if (aVal > bVal) {
          return sortConfig.direction === "asc" ? 1 : -1;
        }
        return 0;
      });
    }

    return filteredData;
  }, [opportunities, filters, columns, sortConfig]);

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInputValue(e.target.value);
  };

  // Clear search
  const clearSearch = () => {
    setSearchInputValue("");
  };

  // Handle sort
  const handleSort = useCallback(
    (key: string) => {
      setSortConfig((prevConfig) => {
        const direction: SortDirection =
          prevConfig.key === key
            ? prevConfig.direction === "asc"
              ? "desc"
              : "asc"
            : "asc";

        // Update filters
        updateFilter("sortColumn", key);
        updateFilter("sortDirection", direction);

        return { key, direction };
      });
    },
    [updateFilter],
  );

  // Render sort icon
  const renderSortIcon = useCallback(
    (column: UnifiedTableColumn<Opportunity>) => {
      if (!column.sortable) return null;

      return (
        <SortIcon
          isActive={sortConfig.key === column.key}
          direction={
            sortConfig.key === column.key ? sortConfig.direction : null
          }
        />
      );
    },
    [sortConfig],
  );

  // Reset sort to defaults
  const resetSortToDefaults = useCallback(() => {
    const defaults = getDefaultSortSettings();
    setSortConfig({
      key: defaults.sortColumn,
      direction: defaults.sortDirection,
    });

    // Update filters
    updateFilter("sortColumn", defaults.sortColumn);
    updateFilter("sortDirection", defaults.sortDirection);
  }, [getDefaultSortSettings, updateFilter]);

  // Handle clear all filters
  const handleClearAllFilters = useCallback(() => {
    clearAllFilters();
    resetSortToDefaults();
    setSearchInputValue("");
  }, [clearAllFilters, resetSortToDefaults]);

  if (!accountId) {
    return (
      <div className="p-4 text-center text-muted-foreground">
        No account selected
      </div>
    );
  }

  return (
    <div className="flex flex-col flex-grow">
      <div className="p-4">
        <div className="flex items-center justify-between gap-4 mb-2">
          {/* Search Input */}
          <div className="relative">
            <Search className="absolute left-0 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Filter by opportunity name..."
              className="w-[250px] border-0 border-b border-border shadow-none rounded-none pl-7 pr-8 h-10 placeholder:text-muted-foreground focus:ring-0 focus-visible:ring-0 focus:border-muted-foreground transition-colors bg-transparent hover:border-muted-foreground"
              onChange={handleSearchChange}
              value={searchInputValue}
            />
            {searchInputValue && (
              <Button
                variant="ghost"
                size="icon"
                className="absolute right-0 top-1/2 -translate-y-1/2 h-6 w-6 p-0 hover:bg-transparent"
                onClick={clearSearch}
              >
                <X className="h-4 w-4 text-muted-foreground hover:text-foreground/80" />
              </Button>
            )}
          </div>
          <InfoTooltip
            trigger={
              <button
                className="ml-2 text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Wildcard search info"
                onClick={(e) => e.stopPropagation()}
              >
                <InfoCircledIcon className="h-3.5 w-3.5" />
              </button>
            }
            side="right"
            sideOffset={5}
          >
            <div className="text-sm space-y-2">
              <p className="font-medium">Supports wildcards</p>
              <ul className="space-y-1">
                <li>
                  <code className="px-1 py-0.5 bg-muted rounded text-xs">
                    _
                  </code>{" "}
                  matches a single character
                </li>
                <li>
                  <code className="px-1 py-0.5 bg-muted rounded text-xs">
                    %
                  </code>{" "}
                  matches zero or more characters
                </li>
              </ul>
            </div>
          </InfoTooltip>

          {/* Results count */}
          <div className="text-sm text-muted-foreground text-right">
            {filteredOpportunities.length}{" "}
            {filteredOpportunities.length === 1
              ? "opportunity"
              : "opportunities"}{" "}
            found
          </div>
        </div>

        {/* Active Filters Display */}
        {hasFilters && (
          <div className="flex items-center mt-2">
            <ActiveFiltersDisplay
              filters={Object.fromEntries(
                Object.entries(filters).filter(
                  ([key]) => key !== "sortColumn" && key !== "sortDirection",
                ),
              )}
              columns={columns}
              clearFilter={clearFilter}
              clearAllFilters={handleClearAllFilters}
              clearAllText="Clear all filters"
              filterType="table"
            />
          </div>
        )}
      </div>

      <div className="rounded-md border overflow-y-auto max-h-[40vh]">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/50">
              <TableHead className="w-12">
                <div className="flex justify-center">
                  <Checkbox
                    checked={
                      filteredOpportunities.length > 0 &&
                      filteredOpportunities.every((opp) =>
                        selectedIds.includes(opp.id),
                      )
                    }
                    onCheckedChange={(checked) => {
                      if (checked) {
                        const allIds = filteredOpportunities.map(
                          (opp) => opp.id,
                        );
                        const newSelectedIds = [...selectedIds];
                        allIds.forEach((id) => {
                          if (!newSelectedIds.includes(id)) {
                            newSelectedIds.push(id);
                          }
                        });
                        onSelectionChange(newSelectedIds);
                      } else {
                        const filteredIds = filteredOpportunities.map(
                          (opp) => opp.id,
                        );
                        onSelectionChange(
                          selectedIds.filter((id) => !filteredIds.includes(id)),
                        );
                      }
                    }}
                    disabled={isLoading || filteredOpportunities.length === 0}
                  />
                </div>
              </TableHead>
              {columns.map((column) => (
                <TableHead
                  key={column.key.toString()}
                  className={cn(
                    column.key === "actions" ? "text-right" : "",
                    column.width,
                  )}
                >
                  <div className="flex items-center justify-between">
                    {column.sortable ? (
                      <div className="flex items-center w-full">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="-ml-3 h-8 data-[state=open]:bg-accent flex items-center font-medium group w-full justify-start hover:bg-muted/50"
                          onClick={() => handleSort(column.key)}
                        >
                          {column.title}
                        </Button>
                        <span
                          className="flex-shrink-0 flex justify-center cursor-pointer"
                          onClick={() => handleSort(column.key)}
                        >
                          {renderSortIcon(column)}
                        </span>
                      </div>
                    ) : (
                      <span className="text-xs flex items-center p-2 rounded-md hover:bg-muted/50">
                        {column.title}
                      </span>
                    )}

                    {column.filterable && (
                      <FilterPopover
                        column={column as UnifiedTableColumn}
                        currentFilter={filters[column.key] as FilterValue}
                        onFilterChange={updateFilter}
                        onClearFilter={clearFilter}
                      />
                    )}
                  </div>
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody className="bg-card">
            {isLoading ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length + 1}
                  className="h-24 text-center"
                >
                  <Loader2 className="h-6 w-6 animate-spin mx-auto" />
                </TableCell>
              </TableRow>
            ) : filteredOpportunities.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length + 1}
                  className="h-24 text-center text-muted-foreground"
                >
                  No opportunities found
                </TableCell>
              </TableRow>
            ) : (
              filteredOpportunities.map((opportunity: Opportunity) => (
                <TableRow key={opportunity.id} className="group">
                  <TableCell className="w-12">
                    <div className="flex justify-center">
                      <Checkbox
                        checked={selectedIds.includes(opportunity.id)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            onSelectionChange([...selectedIds, opportunity.id]);
                          } else {
                            onSelectionChange(
                              selectedIds.filter((id) => id !== opportunity.id),
                            );
                          }
                        }}
                      />
                    </div>
                  </TableCell>
                  <TableCell>{opportunity.name}</TableCell>
                  <TableCell>
                    <Badge
                      variant={getStageVariant(
                        opportunity.is_closed,
                        opportunity.is_won,
                      )}
                    >
                      {opportunity.stage_name}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant="opportunity-type">{opportunity.type}</Badge>
                  </TableCell>
                  <TableCell>
                    {opportunity.created_date
                      ? formatDate(opportunity.created_date)
                      : "-"}
                  </TableCell>
                  <TableCell>
                    {opportunity.close_date
                      ? formatDate(opportunity.close_date)
                      : "-"}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

// Define an extended Person type for the SelectablePersons component
export interface PersonWithTouchpoints extends Person {
  inboundTouchpoints: number;
  outboundTouchpoints: number;
  fullName: string;
}

interface SelectablePersonsProps {
  accountId?: string;
  selectedIds: string[];
  onSelectionChange: (ids: string[]) => void;
}

function SelectablePersons({
  accountId,
  selectedIds,
  onSelectionChange,
}: SelectablePersonsProps) {
  const { persons, isLoading: isLoadingPersons } = useAccountPersons(accountId);
  const { data: activities, isLoading: isLoadingActivities } =
    useAccountActivities(accountId);
  const [searchInputValue, setSearchInputValue] = useState<string>("");
  const debouncedSearchQuery = useDebounce(searchInputValue, 300);

  // Get column definitions
  const columns = useMemo<UnifiedTableColumn[]>(
    () => getPersonColumns() as UnifiedTableColumn[],
    [],
  );

  // Get filter state
  const {
    filters,
    updateFilter,
    clearFilter,
    clearAllFilters,
    hasFilters,
    getDefaultSortSettings,
  } = useCustomBuyingGroupFilters("people");

  // Initialize sort config from filters
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: SortDirection;
  }>({
    key: (filters.sortColumn as string) || getDefaultSortSettings().sortColumn,
    direction:
      (filters.sortDirection as SortDirection) ||
      getDefaultSortSettings().sortDirection,
  });

  // Update filter when debounced search query changes
  useEffect(() => {
    if (debouncedSearchQuery) {
      updateFilter("search", debouncedSearchQuery);
    } else if (filters.search) {
      clearFilter("search");
    }
  }, [debouncedSearchQuery, clearFilter, updateFilter, filters.search]);

  // Calculate touchpoint counts per person
  const allPersonsWithTouchpoints = useMemo(() => {
    if (!persons || !activities) return [];

    const touchpointCounts = activities.reduce(
      (counts, activity) => {
        if (activity.people) {
          activity.people.forEach((person) => {
            // Safely handle activity direction being undefined or any other value
            if (activity.direction === "IN") {
              counts.inbound[person.id] = (counts.inbound[person.id] || 0) + 1;
            } else if (activity.direction === "OUT") {
              counts.outbound[person.id] =
                (counts.outbound[person.id] || 0) + 1;
            } else {
              // For undefined or any other direction value, still count as inbound
              // to ensure all touchpoints are counted
              counts.inbound[person.id] = (counts.inbound[person.id] || 0) + 1;
            }
          });
        }
        return counts;
      },
      { inbound: {}, outbound: {} } as {
        inbound: Record<string, number>;
        outbound: Record<string, number>;
      },
    );

    // Enrich persons with counts and fullName
    return (persons as Person[]).map((person) => ({
      ...person,
      inboundTouchpoints: touchpointCounts.inbound[person.id] || 0,
      outboundTouchpoints: touchpointCounts.outbound[person.id] || 0,
      fullName: `${person.first_name} ${person.last_name}`.trim(),
    }));
  }, [persons, activities]);

  // Filtered persons based on current filters
  const filteredPersons = useMemo<PersonWithTouchpoints[]>(() => {
    if (!allPersonsWithTouchpoints) return [] as PersonWithTouchpoints[];

    let filteredData: PersonWithTouchpoints[] = [...allPersonsWithTouchpoints];

    // Apply search filter
    if (filters.search && typeof filters.search === "string") {
      const searchLower = (filters.search as string).toLowerCase();
      filteredData = filteredData.filter(
        (person) =>
          person.fullName?.toLowerCase().includes(searchLower) ||
          person.email_address?.toLowerCase().includes(searchLower) ||
          person.job_title?.toLowerCase().includes(searchLower),
      );
    }

    // Apply column-specific filters
    Object.entries(filters).forEach(([key, value]) => {
      if (key === "sortColumn" || key === "sortDirection" || key === "search") {
        return;
      }

      const column = columns.find((col) => col.key === key);
      if (!column) return;

      switch (column.filterType) {
        case "text": {
          const text = extractTextFromFilter(value as FilterValue);
          const nullFilter = extractNullFilterFromFilter(value as FilterValue);

          if (nullFilter === "is_null") {
            filteredData = filteredData.filter(
              (person) => person[key as keyof PersonWithTouchpoints] === null,
            );
            break;
          }
          if (nullFilter === "is_not_null") {
            filteredData = filteredData.filter(
              (person) => person[key as keyof PersonWithTouchpoints] !== null,
            );
          }

          if (text) {
            filteredData = filteredData.filter((person) => {
              const personValue = person[key as keyof PersonWithTouchpoints];
              return personValue
                ?.toString()
                .toLowerCase()
                .includes(text.toLowerCase());
            });
          }
          break;
        }
        case "select": {
          const values = extractArrayFromFilter(value as FilterValue);
          const nullFilter = extractNullFilterFromFilter(value as FilterValue);

          if (nullFilter === "is_null") {
            filteredData = filteredData.filter(
              (person) => person[key as keyof PersonWithTouchpoints] === null,
            );
            break;
          }
          if (nullFilter === "is_not_null") {
            filteredData = filteredData.filter(
              (person) => person[key as keyof PersonWithTouchpoints] !== null,
            );
          }

          if (values.length > 0) {
            filteredData = filteredData.filter((person) => {
              const personValue = person[key as keyof PersonWithTouchpoints];
              return personValue && values.includes(personValue.toString());
            });
          }
          break;
        }
        case "number":
          if (typeof value === "object" && value !== null) {
            const numFilter = value as { min?: string; max?: string };
            filteredData = filteredData.filter((person) => {
              const personValue = person[key as keyof PersonWithTouchpoints];
              if (personValue === undefined || personValue === null)
                return false;

              const numValue = Number(personValue);
              let passes = true;

              if (numFilter.min !== undefined && numFilter.min !== "") {
                passes = passes && numValue >= Number(numFilter.min);
              }

              if (numFilter.max !== undefined && numFilter.max !== "") {
                passes = passes && numValue <= Number(numFilter.max);
              }

              return passes;
            });
          }
          break;
      }
    });

    // Apply sorting based on current sort config
    if (sortConfig.key && sortConfig.direction) {
      filteredData.sort(
        (a: PersonWithTouchpoints, b: PersonWithTouchpoints) => {
          const key = sortConfig.key;
          const aValue = a[key as keyof PersonWithTouchpoints];
          const bValue = b[key as keyof PersonWithTouchpoints];

          // Handle nulls: sort nulls last
          if (aValue === null && bValue === null) return 0;
          if (aValue === null) return sortConfig.direction === "asc" ? 1 : -1;
          if (bValue === null) return sortConfig.direction === "asc" ? -1 : 1;

          if (aValue < bValue) {
            return sortConfig.direction === "asc" ? -1 : 1;
          }
          if (aValue > bValue) {
            return sortConfig.direction === "asc" ? 1 : -1;
          }
          return 0;
        },
      );
    }

    return filteredData;
  }, [allPersonsWithTouchpoints, filters, columns, sortConfig]);

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInputValue(e.target.value);
  };

  // Clear search
  const clearSearch = () => {
    setSearchInputValue("");
  };

  // Handle sort
  const handleSort = useCallback(
    (key: string) => {
      setSortConfig((prevConfig) => {
        const direction: SortDirection =
          prevConfig.key === key
            ? prevConfig.direction === "asc"
              ? "desc"
              : "asc"
            : "asc";

        // Update filters
        updateFilter("sortColumn", key);
        updateFilter("sortDirection", direction);

        return { key, direction };
      });
    },
    [updateFilter],
  );

  // Render sort icon
  const renderSortIcon = useCallback(
    (column: UnifiedTableColumn<PersonWithTouchpoints>) => {
      if (!column.sortable) return null;

      return (
        <SortIcon
          isActive={sortConfig.key === column.key}
          direction={
            sortConfig.key === column.key ? sortConfig.direction : null
          }
        />
      );
    },
    [sortConfig],
  );

  // Reset sort to defaults
  const resetSortToDefaults = useCallback(() => {
    const defaults = getDefaultSortSettings();
    setSortConfig({
      key: defaults.sortColumn,
      direction: defaults.sortDirection,
    });

    // Update filters
    updateFilter("sortColumn", defaults.sortColumn);
    updateFilter("sortDirection", defaults.sortDirection);
  }, [getDefaultSortSettings, updateFilter]);

  // Handle clear all filters
  const handleClearAllFilters = useCallback(() => {
    clearAllFilters();
    resetSortToDefaults();
    setSearchInputValue("");
  }, [clearAllFilters, resetSortToDefaults]);

  if (!accountId) {
    return (
      <div className="p-4 text-center text-muted-foreground">
        No account selected
      </div>
    );
  }

  return (
    <div className="flex flex-col flex-grow">
      <div className="p-4">
        <div className="flex items-center justify-between gap-4 mb-2">
          {/* Search Input */}
          <div className="relative">
            <Search className="absolute left-0 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Filter by name, email, or title..."
              className="w-[250px] border-0 border-b border-border shadow-none rounded-none pl-7 pr-8 h-10 placeholder:text-muted-foreground focus:ring-0 focus-visible:ring-0 focus:border-muted-foreground transition-colors bg-transparent hover:border-muted-foreground"
              onChange={handleSearchChange}
              value={searchInputValue}
            />
            {searchInputValue && (
              <Button
                variant="ghost"
                size="icon"
                className="absolute right-0 top-1/2 -translate-y-1/2 h-6 w-6 p-0 hover:bg-transparent"
                onClick={clearSearch}
              >
                <X className="h-4 w-4 text-muted-foreground hover:text-foreground/80" />
              </Button>
            )}
          </div>
          <InfoTooltip
            trigger={
              <button
                className="ml-2 text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Wildcard search info"
                onClick={(e) => e.stopPropagation()}
              >
                <InfoCircledIcon className="h-3.5 w-3.5" />
              </button>
            }
            side="right"
            sideOffset={5}
          >
            <div className="text-sm space-y-2">
              <p className="font-medium">Supports wildcards</p>
              <ul className="space-y-1">
                <li>
                  <code className="px-1 py-0.5 bg-muted rounded text-xs">
                    _
                  </code>{" "}
                  matches a single character
                </li>
                <li>
                  <code className="px-1 py-0.5 bg-muted rounded text-xs">
                    %
                  </code>{" "}
                  matches zero or more characters
                </li>
              </ul>
            </div>
          </InfoTooltip>

          {/* Results count */}
          <div className="text-sm text-muted-foreground text-right">
            {filteredPersons.length}{" "}
            {filteredPersons.length === 1 ? "person" : "people"} found
          </div>
        </div>

        {/* Active Filters Display */}
        {hasFilters && (
          <div className="flex items-center mt-2">
            <ActiveFiltersDisplay
              filters={Object.fromEntries(
                Object.entries(filters).filter(
                  ([key]) => key !== "sortColumn" && key !== "sortDirection",
                ),
              )}
              columns={columns}
              clearFilter={clearFilter}
              clearAllFilters={handleClearAllFilters}
              clearAllText="Clear all filters"
              filterType="table"
            />
          </div>
        )}
      </div>

      <div className="rounded-md border overflow-y-auto max-h-[40vh]">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/50">
              <TableHead className="w-12">
                <div className="flex justify-center">
                  <Checkbox
                    checked={
                      filteredPersons.length > 0 &&
                      filteredPersons.every((person) =>
                        selectedIds.includes(person.id),
                      )
                    }
                    onCheckedChange={(checked) => {
                      if (checked) {
                        const allIds = filteredPersons.map(
                          (person) => person.id,
                        );
                        const newSelectedIds = [...selectedIds];
                        allIds.forEach((id) => {
                          if (!newSelectedIds.includes(id)) {
                            newSelectedIds.push(id);
                          }
                        });
                        onSelectionChange(newSelectedIds);
                      } else {
                        const filteredIds = filteredPersons.map(
                          (person) => person.id,
                        );
                        onSelectionChange(
                          selectedIds.filter((id) => !filteredIds.includes(id)),
                        );
                      }
                    }}
                    disabled={
                      isLoadingPersons ||
                      isLoadingActivities ||
                      filteredPersons.length === 0
                    }
                  />
                </div>
              </TableHead>
              {columns.map((column) => (
                <TableHead
                  key={column.key.toString()}
                  className={cn(
                    column.key === "actions" ? "text-right" : "",
                    column.width,
                  )}
                >
                  <div className="flex items-center justify-between">
                    {column.sortable ? (
                      <div className="flex items-center w-full">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="-ml-3 h-8 data-[state=open]:bg-accent flex items-center font-medium group w-full justify-start hover:bg-muted/50"
                          onClick={() => handleSort(column.key)}
                        >
                          {column.title}
                        </Button>
                        <span
                          className="flex-shrink-0 flex justify-center cursor-pointer"
                          onClick={() => handleSort(column.key)}
                        >
                          {renderSortIcon(column)}
                        </span>
                      </div>
                    ) : (
                      <span className="text-xs flex items-center p-2 rounded-md hover:bg-muted/50">
                        {column.title}
                      </span>
                    )}

                    {column.filterable && (
                      <FilterPopover
                        column={column as UnifiedTableColumn}
                        currentFilter={filters[column.key] as FilterValue}
                        onFilterChange={updateFilter}
                        onClearFilter={clearFilter}
                      />
                    )}
                  </div>
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody className="bg-card">
            {isLoadingPersons || isLoadingActivities ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length + 1}
                  className="h-24 text-center"
                >
                  <Loader2 className="h-6 w-6 animate-spin mx-auto" />
                </TableCell>
              </TableRow>
            ) : filteredPersons.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length + 1}
                  className="h-24 text-center text-muted-foreground"
                >
                  No people found
                </TableCell>
              </TableRow>
            ) : (
              filteredPersons.map((person: PersonWithTouchpoints) => (
                <TableRow key={person.id} className="group">
                  <TableCell className="w-12">
                    <div className="flex justify-center">
                      <Checkbox
                        checked={selectedIds.includes(person.id)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            onSelectionChange([...selectedIds, person.id]);
                          } else {
                            onSelectionChange(
                              selectedIds.filter((id) => id !== person.id),
                            );
                          }
                        }}
                      />
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2 w-full">
                      {person.first_name} {person.last_name}
                    </div>
                  </TableCell>
                  <TableCell>
                    {person.email_address && (
                      <a
                        href={`mailto:${person.email_address}`}
                        className="text-primary"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {person.email_address}
                      </a>
                    )}
                  </TableCell>
                  <TableCell>{person.job_title}</TableCell>
                  <TableCell>{person.inboundTouchpoints || 0}</TableCell>
                  <TableCell>{person.outboundTouchpoints || 0}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

export interface CustomBuyingGroupModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (name: string, opportunityIds: string[], personIds: string[]) => void;
  onClear: () => void;
  initialGroup?: CustomBuyingGroup | null;
}

export function CustomBuyingGroupModal({
  isOpen,
  onOpenChange,
  onSave,
  onClear,
  initialGroup = null,
}: CustomBuyingGroupModalProps) {
  // Account selection
  const { selectedAccountId } = useAccountSelection();

  // State
  const [name, setName] = useState("");
  const [selectedOpportunityIds, setSelectedOpportunityIds] = useState<
    string[]
  >([]);
  const [selectedPersonIds, setSelectedPersonIds] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const [activeTab, setActiveTab] = useState<"people" | "opportunities">(
    "people",
  );

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      if (initialGroup) {
        setName(initialGroup.name);
        setSelectedOpportunityIds(initialGroup.opportunityIds);
        setSelectedPersonIds(initialGroup.personIds);
      } else {
        setName("");
        setSelectedOpportunityIds([]);
        setSelectedPersonIds([]);
      }
      // Reset active tab to default when opening
      setActiveTab("people");
    } else {
      // Clean up state when modal closes
      setName("");
      setSelectedOpportunityIds([]);
      setSelectedPersonIds([]);
      setActiveTab("people");
    }
  }, [isOpen, initialGroup]);

  // Handle save
  const handleSave = async () => {
    if (!name.trim()) return;

    setIsSaving(true);
    try {
      await onSave(name.trim(), selectedOpportunityIds, selectedPersonIds);
      onOpenChange(false);
    } finally {
      setIsSaving(false);
    }
  };

  // Handle delete
  const handleDelete = async () => {
    setIsClearing(true);
    try {
      // Only call onClear (which is deleteCustomGroup) if we have an initialGroup
      if (initialGroup) {
        await onClear();
      }
      onOpenChange(false);
    } finally {
      setIsClearing(false);
    }
  };

  // Display selection counts
  const selectionCounts = useMemo(
    () => (
      <div className="flex items-center text-sm rounded-md bg-muted px-4 py-2 flex-shrink-0">
        <span className="font-medium mr-4">Selected:</span>
        <div className="flex gap-6">
          <span>
            <strong className="font-semibold">
              {selectedOpportunityIds.length}
            </strong>{" "}
            {selectedOpportunityIds.length === 1
              ? "opportunity"
              : "opportunities"}
          </span>
          <span>
            <strong className="font-semibold">
              {selectedPersonIds.length}
            </strong>{" "}
            {selectedPersonIds.length === 1 ? "person" : "people"}
          </span>
        </div>
      </div>
    ),
    [selectedOpportunityIds.length, selectedPersonIds.length],
  );

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(newOpen) => {
        // Prevent closing during save/clear operations
        if (!newOpen && (isSaving || isClearing)) {
          return;
        }
        onOpenChange(newOpen);
      }}
    >
      <DialogContent className="w-full max-w-[90vw] max-h-[90vh] flex flex-col bg-card">
        <DialogHeader>
          <DialogTitle className="text-xl">
            {initialGroup
              ? "Edit Custom Buying Group"
              : "Define Custom Buying Group"}
          </DialogTitle>
          <DialogDescription>
            Select the people and opportunities you want to include in your
            custom buying group.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4 flex-grow min-h-0">
          <div className="flex items-center gap-4 px-4">
            <div className="flex-grow flex flex-col gap-1 max-w-md">
              <div className="flex items-center gap-2">
                <Label htmlFor="name" className="flex-shrink-0">
                  Group Name <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className={cn(
                    "flex-grow",
                    !name.trim() &&
                      "border-destructive/50 focus-visible:ring-destructive/50",
                  )}
                  placeholder="Enter a name for this buying group (required)"
                  aria-required="true"
                />
              </div>
              {!name.trim() && (
                <p className="text-xs text-destructive ml-[81px]">
                  Group name is required
                </p>
              )}
            </div>
            {selectionCounts}
          </div>

          <div className="flex border-b px-4">
            <div className="flex gap-6">
              <button
                className={cn(
                  "py-3 text-sm font-medium",
                  "hover:text-primary/90 transition-colors",
                  "focus:outline-none",
                  activeTab === "people"
                    ? "text-primary border-b-2 border-primary font-semibold"
                    : "text-muted-foreground",
                )}
                onClick={() => setActiveTab("people")}
              >
                People
              </button>
              <button
                className={cn(
                  "py-3 text-sm font-medium",
                  "hover:text-primary/90 transition-colors",
                  "focus:outline-none",
                  activeTab === "opportunities"
                    ? "text-primary border-b-2 border-primary font-semibold"
                    : "text-muted-foreground",
                )}
                onClick={() => setActiveTab("opportunities")}
              >
                Opportunities
              </button>
            </div>
          </div>

          <div className="flex-grow min-h-0 px-4 pb-4 overflow-hidden">
            {activeTab === "people" ? (
              <SelectablePersons
                accountId={selectedAccountId}
                selectedIds={selectedPersonIds}
                onSelectionChange={setSelectedPersonIds}
              />
            ) : (
              <SelectableOpportunities
                accountId={selectedAccountId}
                selectedIds={selectedOpportunityIds}
                onSelectionChange={setSelectedOpportunityIds}
              />
            )}
          </div>
        </div>

        <DialogFooter className="flex justify-end pt-2 px-4 pb-4 gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSaving || isClearing}
          >
            Cancel
          </Button>

          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={isSaving || isClearing || !initialGroup}
          >
            {isClearing ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : null}
            Delete Buying Group
          </Button>

          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div>
                  {" "}
                  {/* Wrap in div to allow tooltip on disabled button */}
                  <Button
                    onClick={handleSave}
                    disabled={
                      name.trim().length === 0 ||
                      (selectedOpportunityIds.length === 0 &&
                        selectedPersonIds.length === 0) ||
                      isSaving ||
                      isClearing
                    }
                  >
                    {isSaving ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : null}
                    Save Buying Group
                  </Button>
                </div>
              </TooltipTrigger>
              {!name.trim() ? (
                <TooltipContent>
                  Please enter a name for your buying group
                </TooltipContent>
              ) : selectedOpportunityIds.length === 0 &&
                selectedPersonIds.length === 0 ? (
                <TooltipContent>
                  Please select at least one person or opportunity
                </TooltipContent>
              ) : null}
            </Tooltip>
          </TooltipProvider>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
